import axios from 'axios';
import { serialize } from 'object-to-formdata';

import { setImpersonationExpiresAt } from '../../utils/helpers/storage';

import { shouldTransformOutgoingMethod, transformIncomingData, transformOutgoingData } from './dataTransform';
import { ApiResponse, transformError, transformSuccess } from './responseTransform';

export const axiosClient = axios.create({
  baseURL: `https://${window.env?.RIDEIQ_BASE_DOMAIN}/rideiq/v1/`,
  headers: {
    'Referrer-Policy': 'strict-origin-when-cross-origin',
    'x-rideiq-bundle-version': __webpack_hash__,
  },
});

axiosClient.interceptors.request.use((req) => {
  req.withCredentials = req.withCredentials;

  req.headers['X-Api-Version'] = window.env?.RIDEIQ_API_VERSION || '';

  req.headers.set(Object.fromEntries(Object.entries(req.headers).filter(([_, value]) => value)));

  return req;
});

axiosClient.interceptors.request.use((req) => {
  req.params = transformOutgoingData(req.params);

  return req;
});

axiosClient.interceptors.request.use((req) => {
  if (shouldTransformOutgoingMethod(req.method) && req.data) {
    req.data = transformOutgoingData(req.data);
  }

  return req;
});

axiosClient.interceptors.response.use(
  (resp) => {
    if (resp.headers['x-impersonation-expires-at']) {
      setImpersonationExpiresAt(resp.headers['x-impersonation-expires-at']);
    }
    if (resp.data) {
      resp.data = transformIncomingData(resp.data);
    }

    return resp;
  },
  (error) => {
    // We really want to throw the error so it is handled and we
    // don't get an unhandledrejection error. By throwing here, we
    // are handling the rejection, and bubbling up to the closest
    // error handler (try/catch or catch method call on a promise).
    throw error;
  }
);

export default axiosClient;
export const getRequest = <T>(URL: string, signal?: AbortSignal): Promise<ApiResponse<T>> =>
  axiosClient
    .get<T>(`/${URL}`, { signal })
    .then((response) => transformSuccess<T>(response))
    .catch((error) => transformError(error));

export const getBlobRequest = <T>(
  URL: string,
  options?: { signal?: AbortSignal; localErrorHandling?: boolean }
): Promise<ApiResponse<T>> =>
  axiosClient
    .get<T>(`/${URL}`, { responseType: 'blob', ...options })
    .then((response) => transformSuccess<T>(response))
    .catch((error) => transformError(error));

export const getWithParamsRequest = <T>(
  URL: string,
  queryParams,
  signal?: AbortSignal,
  options?: { localErrorHandling?: boolean }
): Promise<ApiResponse<T>> =>
  axiosClient
    .get<T>(`/${URL}`, { params: queryParams, signal: signal, ...options })
    .then((response) => transformSuccess<T>(response))
    .catch((error) => transformError(error));

export const postRequest = <T, K>(
  URL: string,
  payload,
  signal?: AbortSignal,
  skipGlobalErrorHandling?: boolean
): Promise<ApiResponse<T, K>> =>
  axiosClient
    .post(`/${URL}`, payload, { signal: signal, skipGlobalErrorHandling })
    .then((response) => transformSuccess<T>(response))
    .catch((error) => transformError<T, K>(error));
export const postRequestWithFormData = <T, K>(URL: string, payload): Promise<ApiResponse<T, K>> =>
  axiosClient
    .post(`/${URL}`, serialize(transformOutgoingData(payload)))
    .then((response) => transformSuccess<T>(response))
    .catch((error) => transformError(error));
export const putRequest = <T>(URL: string, payload, signal?: AbortSignal): Promise<ApiResponse<T>> =>
  axiosClient
    .put(`/${URL}`, payload, { signal: signal })
    .then((response) => transformSuccess<T>(response))
    .catch((error) => transformError(error));
export const putRequestWithFormData = <T>(URL: string, payload): Promise<ApiResponse<T>> =>
  axiosClient
    .put(`/${URL}`, serialize(transformOutgoingData(payload)))
    .then((response) => transformSuccess<T>(response))
    .catch((error) => transformError(error));
export const deleteRequest = <T>(URL: string, options?: { localErrorHandling?: boolean }): Promise<ApiResponse<T>> =>
  axiosClient
    .delete(`/${URL}`, options)
    .then((response) => transformSuccess<T>(response))
    .catch((error) => transformError(error));
export const patchRequest = <T>(URL: string, payload, signal?: AbortSignal): Promise<ApiResponse<T>> =>
  axiosClient
    .patch(`/${URL}`, payload, { signal: signal })
    .then((response) => transformSuccess<T>(response))
    .catch((error) => transformError(error));
export const patchRequestWithFormData = <T>(URL, payload): Promise<ApiResponse<T>> =>
  axiosClient
    .patch(`/${URL}`, serialize(transformOutgoingData(payload)))
    .then((response) => transformSuccess<T>(response))
    .catch((error) => transformError(error));
